import { Link, useLocation, useNavigate } from "react-router-dom";
import { Users, LayoutDashboard, UserPlus, Download, Archive, LogOut } from "lucide-react";
import { Button } from "./ui/button";
import { patientApi } from "../services/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, admin } = useAuth();

  const isActive = (path) => location.pathname === path || (path !== "/admin/dashboard" && location.pathname.startsWith(path));

  const handleExport = async () => {
    try {
      const blob = await patientApi.exportCSV();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "sportivi_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export realizat cu succes!");
    } catch (error) {
      toast.error("Eroare la export");
      console.error("Export error:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Deconectat cu succes");
    // Folosim full reload pentru a evita conflictul cu ProtectedRoute redirect
    window.location.replace("/");
  };

  const navLinks = [
    { path: "/admin/dashboard", label: "Panou", icon: LayoutDashboard },
    { path: "/admin/patients", label: "Sportivi", icon: Users },
    { path: "/admin/add-patient", label: "Adaugă Sportiv", icon: UserPlus },
  ];

  return (
    <nav
      data-testid="navbar"
      className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link
            to="/admin/dashboard"
            data-testid="logo-link"
            className="flex items-center gap-2"
          >
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg">M</span>
            </div>
            <span className="font-semibold text-xl tracking-tight hidden sm:block" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              MedSport
            </span>
          </Link>

          {/* Navigation Links */}
          <div className="flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link key={link.path} to={link.path}>
                  <Button
                    data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
                    variant={isActive(link.path) ? "default" : "ghost"}
                    size="sm"
                    className="gap-2"
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{link.label}</span>
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* Action buttons (Arhiva + Export) */}
          <div className="flex items-center gap-2">
            {/* Arhiva Button - redirect către arhiva externă */}
            <Button
              data-testid="arhiva-btn"
              variant="outline"
              size="sm"
              asChild
              className="gap-2"
            >
              <a href="http://hosting2077910.online.pro/pacienti" target="_blank" rel="noopener noreferrer">
                <Archive className="w-4 h-4" />
                <span className="hidden sm:inline">Arhivă</span>
              </a>
            </Button>

            {/* Export Button */}
            <Button
              data-testid="export-btn"
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </Button>

            {/* Logout */}
            {admin && (
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-2 text-slate-600 hover:text-red-700 hover:bg-red-50"
                title={`Deconectare ${admin.username}`}
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Ieșire</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
